/*!
 * cart-page.js — Carolyn Fabrics full cart page enhancements.
 *
 * Scope: ONLY the /cart page (section "main-cart"). Does not touch the cart
 * drawer (that lives in global.js and uses different attributes), the
 * configurator, or any product/pricing logic.
 *
 * v2 (2026-08-16): optimistic quantity edits, debounced batched save.
 *   - Steppers / typed edits update the input immediately (no dropped taps,
 *     no lock while a request is in flight).
 *   - All pending changes are collected and sent as ONE POST /cart/update.js
 *     (updates[] via FormData) ~700ms after the last edit, followed by ONE
 *     reload so the Liquid-rendered totals re-run with the new cart state.
 *   - Remove (X) sets the row's quantity input to 0 and saves on the same
 *     debounce path.
 *
 * No-JS fallback: the existing <form id="cart-form"> posts to /cart with
 * updates[] fields; a visible "Update Cart" submit button ships in the
 * template, so users without JS can type a quantity (0 to remove) and submit.
 */
(function () {
  "use strict";

  var form = document.getElementById("cart-form");
  if (!form) return; // not on the cart page, bail

  var DEBOUNCE_MS = 700;
  var saveTimer = null;
  var saving = false;   // a save POST is in the air
  var dirty = false;    // edits arrived while saving — save again after

  // Minimal CSS.escape polyfill for attribute selectors containing ':' etc.
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(s);
    return String(s).replace(/([^a-zA-Z0-9_-])/g, "\\$1");
  }

  function markUpdating(key, on) {
    var row = form.querySelector('[data-cart-item="' + cssEscape(key) + '"]');
    if (row) row.classList.toggle("is-updating", !!on);
  }

  function refreshCountBadge(cart) {
    document.querySelectorAll("#cart-count, .cart-count").forEach(function (el) {
      el.textContent = cart.item_count;
      if (cart.item_count === 0) el.classList.add("cart-count--hidden");
      else el.classList.remove("cart-count--hidden");
    });
  }

  function scheduleSave() {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(save, DEBOUNCE_MS);
  }

  function save() {
    saveTimer = null;
    if (saving) { dirty = true; return; }
    saving = true;
    // Serialize the whole form: updates[] carries every line's current
    // quantity (including any set to 0), note ships along for free.
    var updates = {};
    form.querySelectorAll("[data-item-qty-input]").forEach(function (input) {
      updates[input.getAttribute("data-item-qty-input")] = Math.max(0, parseInt(input.value, 10) || 0);
    });
    var note = form.querySelector('[name="note"]');
    var payload = { updates: updates };
    if (note) payload.note = note.value;
    fetch("/cart/update.js", {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) throw new Error("cart/update failed: " + r.status);
        return r.json();
      })
      .then(function (cart) { return window.CFKitGuard ? window.CFKitGuard.reconcile(cart) : cart; })
      .then(function (cart) {
        saving = false;
        if (dirty) { dirty = false; save(); return; } // late edits — flush first
        refreshCountBadge(cart);
        window.location.reload();
      })
      .catch(function (err) {
        console.error("[cart-page]", err);
        saving = false;
        dirty = false;
        form.querySelectorAll(".is-updating").forEach(function (el) {
          el.classList.remove("is-updating");
        });
        alert("Could not update cart. Please click Update Cart to try again.");
      });
  }

  function inputForKey(key) {
    return form.querySelector('[data-item-qty-input="' + cssEscape(key) + '"]');
  }

  function setQty(key, qty) {
    var input = inputForKey(key);
    if (!input) return;
    input.value = qty;
    markUpdating(key, true);
    scheduleSave();
  }

  // --- Click handler: remove X, qty minus, qty plus (optimistic, never locked) ---
  form.addEventListener("click", function (e) {
    var rm = e.target.closest("[data-remove-item]");
    if (rm) {
      e.preventDefault();
      setQty(rm.getAttribute("data-remove-item"), 0);
      return;
    }
    var minus = e.target.closest("[data-item-qty-minus]");
    if (minus) {
      e.preventDefault();
      var mKey = minus.getAttribute("data-item-qty-minus");
      var mInput = inputForKey(mKey);
      if (!mInput) return;
      var mCur = parseInt(mInput.value, 10) || 0;
      setQty(mKey, Math.max(0, mCur - 1));
      return;
    }
    var plus = e.target.closest("[data-item-qty-plus]");
    if (plus) {
      e.preventDefault();
      var pKey = plus.getAttribute("data-item-qty-plus");
      var pInput = inputForKey(pKey);
      if (!pInput) return;
      var pCur = parseInt(pInput.value, 10) || 0;
      setQty(pKey, pCur + 1);
      return;
    }
  });

  // --- Change handler: direct quantity input edits ---
  form.addEventListener("change", function (e) {
    var input = e.target.closest("[data-item-qty-input]");
    if (!input) return;
    var key = input.getAttribute("data-item-qty-input");
    var qty = Math.max(0, parseInt(input.value, 10) || 0);
    input.value = qty; // normalize
    markUpdating(key, true);
    scheduleSave();
  });

  // --- "Update Cart" submit button: flush immediately via the same path. ---
  form.addEventListener("submit", function (e) {
    var updateBtn = form.querySelector("[data-cart-update]");
    var submitter = e.submitter || updateBtn;
    if (!submitter || !submitter.hasAttribute("data-cart-update")) return;
    e.preventDefault();
    if (saveTimer) clearTimeout(saveTimer);
    if (saving) { dirty = true; return; }
    save();
  });
})();
